import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tags, Save } from "lucide-react";
import { API } from "@/lib/api";

const PriceInput = ({ cents, onChange, testId }) => (
  <div className="relative">
    <Input type="number" min="0" step="1" value={cents != null ? (cents / 100).toString() : ""}
      onChange={(e) => onChange(Math.max(0, Math.round(parseFloat(e.target.value || "0") * 100)))}
      className="border-[var(--brand-border)] w-28 pr-7 text-right" data-testid={testId} />
    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-[var(--brand-muted)]">€</span>
  </div>
);

export const PricingCard = () => {
  const [pricing, setPricing] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/platform/pricing`).then((r) => setPricing(r.data)).catch(() => {});
  }, []);

  const setPlan = (pid, field, cents) =>
    setPricing((p) => ({ ...p, plans: { ...p.plans, [pid]: { ...p.plans[pid], [field]: cents } } }));
  const setTier = (mid, tid, field, cents) =>
    setPricing((p) => ({
      ...p,
      modules: {
        ...p.modules,
        [mid]: { ...p.modules[mid], tiers: { ...p.modules[mid].tiers, [tid]: { ...p.modules[mid].tiers[tid], [field]: cents } } },
      },
    }));
  const setBundle = (bundleId, field, cents) =>
    setPricing((p) => ({ ...p, bundles: { ...p.bundles, [bundleId]: { ...p.bundles[bundleId], [field]: cents } } }));

  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/platform/pricing`, pricing);
      setPricing(r.data);
      toast.success("Preise gespeichert – sofort aktiv, ohne Code-Änderung");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setBusy(false); }
  };

  if (!pricing) return null;
  const tierOrder = ["basic", "business", "premium"];
  return (
    <Card className="p-5 border-[var(--brand-border)]" data-testid="pricing-card">
      <div className="flex items-start gap-2 mb-3">
        <Tags className="w-5 h-5 text-[var(--brand-primary)] mt-0.5" />
        <div>
          <div className="text-sm font-semibold text-[var(--brand-ink)]">Preisverwaltung</div>
          <p className="text-xs text-[var(--brand-muted)]">Alle Preise werden in der Datenbank gespeichert und können hier jederzeit geändert werden – ohne Programmieraufwand. Preise netto, zzgl. MwSt.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-[var(--brand-muted)] mb-2">Basistarife (Business Studio Suite)</div>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[var(--brand-muted)]"><th className="py-1 pr-2">Tarif</th><th className="py-1 pr-2">Monat</th><th className="py-1">Jahr</th></tr></thead>
            <tbody>
              {Object.entries(pricing.plans).map(([pid, p]) => (
                <tr key={pid}>
                  <td className="py-1.5 pr-2 font-medium text-[var(--brand-ink)]">{p.name || pid}</td>
                  <td className="py-1.5 pr-2"><PriceInput cents={p.month} onChange={(c) => setPlan(pid, "month", c)} testId={`price-plan-${pid}-month`} /></td>
                  <td className="py-1.5"><PriceInput cents={p.year} onChange={(c) => setPlan(pid, "year", c)} testId={`price-plan-${pid}-year`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {Object.entries(pricing.modules).map(([mid, m]) => (
          <div key={mid}>
            <div className="text-xs font-semibold uppercase tracking-wide text-[var(--brand-muted)] mb-2">{m.name || mid}</div>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[var(--brand-muted)]"><th className="py-1 pr-2">Paket</th><th className="py-1 pr-2">Monat</th><th className="py-1">Jahr</th></tr></thead>
              <tbody>
                {tierOrder.filter((t) => m.tiers && m.tiers[t]).map((tid) => (
                  <tr key={tid}>
                    <td className="py-1.5 pr-2 font-medium text-[var(--brand-ink)]">{m.tiers[tid].name || tid}</td>
                    <td className="py-1.5 pr-2"><PriceInput cents={m.tiers[tid].month} onChange={(c) => setTier(mid, tid, "month", c)} testId={`price-${mid}-${tid}-month`} /></td>
                    <td className="py-1.5"><PriceInput cents={m.tiers[tid].year} onChange={(c) => setTier(mid, tid, "year", c)} testId={`price-${mid}-${tid}-year`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
      {Object.entries(pricing.bundles || {}).length > 0 && (
        <div className="mt-6 rounded-lg border border-[var(--brand-border)] p-4" data-testid="pricing-bundles">
          <div className="text-xs font-semibold uppercase tracking-wide text-[var(--brand-muted)] mb-2">Gemeinsame Buchungsvarianten</div>
          <p className="mb-3 text-xs text-[var(--brand-muted)]">Bundles führen keine neuen Studios ein. Sie bündeln ausschließlich bereits vorhandene Studio-Pakete.</p>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[var(--brand-muted)]"><th className="py-1 pr-2">Bundle</th><th className="py-1 pr-2">Enthalten</th><th className="py-1 pr-2">Monat</th><th className="py-1">Jahr</th></tr></thead>
            <tbody>
              {Object.entries(pricing.bundles).map(([bundleId, bundle]) => (
                <tr key={bundleId}>
                  <td className="py-1.5 pr-2 font-medium text-[var(--brand-ink)]">{bundle.name || bundleId}</td>
                  <td className="py-1.5 pr-2 text-xs text-[var(--brand-muted)]">{bundle.description || "—"}</td>
                  <td className="py-1.5 pr-2"><PriceInput cents={bundle.month} onChange={(c) => setBundle(bundleId, "month", c)} testId={`price-bundle-${bundleId}-month`} /></td>
                  <td className="py-1.5"><PriceInput cents={bundle.year} onChange={(c) => setBundle(bundleId, "year", c)} testId={`price-bundle-${bundleId}-year`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Button onClick={save} disabled={busy} className="mt-4 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="pricing-save-btn">
        <Save className="w-4 h-4 mr-1.5" /> {busy ? "Speichern…" : "Preise speichern"}
      </Button>
    </Card>
  );
};
